import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KanbanTask, TaskStatus } from '../types';
import { Shield, Users, Lock, Sparkles, Loader2, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAiAction } from '@/hooks/useAiAction';

interface TaskCardProps {
  task: KanbanTask;
  onMove: (taskId: string, newStatus: TaskStatus) => void;
  onClick?: (task: KanbanTask) => void;
  householdId?: string | null;
  onAddSubtasks?: (taskId: string, subtasks: string[]) => void;
}

/**
 * 🎴 TaskCard Component
 * Refactored for Hybrid Scoping: Visualizes data partition and enforces privacy cues.
 */
export const TaskCard: React.FC<TaskCardProps> = ({ task, onMove, onClick, householdId, onAddSubtasks }) => {
  const { t } = useTranslation();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [suggestedSubtasks, setSuggestedSubtasks] = useState<string[]>([]);
  const { callAction: callAiAction, isLoading: aiLoading } = useAiAction(householdId ?? null);

  const handleBreakdown = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (suggestedSubtasks.length > 0) {
      setShowBreakdown(s => !s);
      return;
    }
    const result = await callAiAction<{ subTasks: string[] }>('breakdownTask', {
      taskTitle: task.title,
      taskDescription: task.description,
    });
    if (result?.subTasks?.length) {
      setSuggestedSubtasks(result.subTasks);
      setShowBreakdown(true);
    }
  };

  // 🛡️ Determine Scope Context
  const isPersonal = task.scope === 'personal' || !task.scope;

  // 🛰️ DND-KIT SORTABLE HOOK
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: task.id,
    data: {
      task // Passing full task for scoped migration logic
    }
  });

  // 🧪 DYNAMIC STYLING
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 1,
  };

  const isGrocery = task.templateType === 'grocery';
  const isAction = task.templateType === 'action';

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'P1': return 'bg-rose-500';
      case 'P2': return 'bg-amber-500';
      case 'P3': return 'bg-blue-500';
      default: return 'bg-slate-400';
    }
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        e.stopPropagation(); // Prevent the DndContext from intercepting the click prematurely

        if (!isDragging) {
          onClick?.(task);
        }
      }}
      className={`bg-white dark:bg-slate-900 border rounded-2xl p-4 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing mb-3 group ${
        isDragging ? 'ring-2 ring-brand shadow-xl scale-[1.02]' : ''
      } ${
        // 🛡️ Visual border cue for partition origin
        isPersonal 
          ? 'border-emerald-100 dark:border-emerald-900/30' 
          : 'border-slate-200 dark:border-slate-800'
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-start gap-2">
          {/* 🛡️ SCOPE INDICATOR: Essential for Google Data Privacy Policy compliance */}
          <div className={`mt-0.5 p-1 rounded-md ${
            isPersonal 
              ? 'bg-emerald-50 text-emerald-500 dark:bg-emerald-900/20' 
              : 'bg-indigo-50 text-brand dark:bg-indigo-900/20'
          }`}>
            {isPersonal ? <Shield className="w-2.5 h-2.5" /> : <Users className="w-2.5 h-2.5" />}
          </div>
          
          <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 tracking-tight leading-tight">
            {task.title}
          </h3>
        </div>
        
        <div className="flex items-center gap-2">
          {isAction && task.priorityTier && task.priorityTier !== 'none' && (
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1 ${getPriorityColor(task.priorityTier)}`} />
          )}
          {/* 🛰️ DRAG HANDLE: Only this element will initiate drag operations */}
          <div 
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 cursor-grab text-slate-400 dark:text-slate-600 transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="5" r="1"></circle>
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="12" cy="19" r="1"></circle>
            </svg>
          </div>
        </div>
      </div>

      {task.description && (
        <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 line-clamp-2 mb-3 leading-relaxed">
          {task.description}
        </p>
      )}

      {/* --- CONDITIONAL UI SLOTS --- */}
      <div className="flex flex-wrap gap-1.5 items-center">
        {/* 🛡️ PARTITION BADGE: Textual confirmation of data silo */}
        <div className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border ${
          isPersonal 
            ? 'text-emerald-500 border-emerald-200 dark:border-emerald-800' 
            : 'text-slate-400 border-slate-200 dark:border-slate-800'
        }`}>
                      {isPersonal ? t('taskCard.private', 'Private') : t('taskCard.shared', 'Shared')}
        </div>

        {isGrocery && task.consumption && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-lg text-2xs font-black uppercase tracking-tighter flex items-center gap-1 border border-emerald-100 dark:border-emerald-800">
            <span>{task.consumption.quantity}</span>
            <span>{task.consumption.unit}</span>
          </div>
        )}

        {isAction && task.energyRequired !== undefined && (
          <div className="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 px-2 py-0.5 rounded-lg text-2xs font-black uppercase tracking-tighter flex items-center gap-1 border border-indigo-100 dark:border-indigo-800">
            <span className="opacity-70">⚡</span>
            <span>{task.energyRequired}/10</span>
          </div>
        )}

        {task.recurrence?.isEnabled && (
          <div className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-lg text-2xs font-black uppercase tracking-tighter flex items-center gap-1">
            <span className="text-[8px]">🔄</span>
            <span>
              {task.recurrence.completionTrigger
                ? 'After Completion'
                : task.recurrence.interval === 1
                ? task.recurrence.frequency?.replace(/ly$/, '') || ''
                : `Every ${task.recurrence.interval} ${
                    task.recurrence.frequency?.replace(/ly$/, 's') || ''
                  }`}
            </span>
          </div>
        )}
      </div>

      {/* --- AI BREAKDOWN --- */}
      {task.templateType === 'action' && householdId && (
        <div className="mt-2">
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handleBreakdown}
            disabled={aiLoading}
            className="flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-brand hover:text-indigo-700 transition-colors"
          >
            {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 text-amber-500" />}
            Break down
            {suggestedSubtasks.length > 0 && <ChevronDown className={`w-3 h-3 transition-transform ${showBreakdown ? 'rotate-180' : ''}`} />}
          </button>
          {showBreakdown && suggestedSubtasks.length > 0 && (
            <div className="mt-2 space-y-1">
              {suggestedSubtasks.map((st, i) => (
                <div key={i} className="text-2xs text-slate-600 dark:text-slate-300 flex items-start gap-1">
                  <span className="text-indigo-400 shrink-0">•</span>
                  <span>{st}</span>
                </div>
              ))}
              {onAddSubtasks && (
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onAddSubtasks(task.id, suggestedSubtasks); setShowBreakdown(false); }}
                  className="text-[9px] font-black uppercase tracking-wider px-2 py-1 bg-indigo-500 text-white rounded-lg hover:bg-brand transition-colors mt-1"
                >
                  Add as subtasks
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- QUICK ACTIONS --- */}
      <div className="mt-4 pt-3 border-t border-slate-50 dark:border-slate-800/50 flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {task.status !== 'history' && (
          <button 
            onPointerDown={(e) => e.stopPropagation()} 
            onClick={(e) => {
              e.stopPropagation();
              const nextStatus: TaskStatus = isGrocery && task.status === 'pantry' ? 'history' : 'done';
              onMove(task.id, nextStatus);
            }}
            className="text-2xs font-black uppercase tracking-widest px-3 py-1.5 rounded-xl transition-colors bg-[#ffde5a]/20 text-[#0f1117] dark:text-[#ffde5a] hover:bg-[#ffde5a]/40"
          >
{isGrocery && task.status === 'pantry' ? t('taskCard.quickActions.archive', 'Archive') : t('taskCard.quickActions.done', 'Done')}
          </button>
        )}
      </div>
    </div>
  );
};